import { Home, Clapperboard, MessageCircle, Sparkles, User } from "lucide-react";

// The core zones. ALIX (cognitive anchor) and FEED (content home) are pinned
// to the bar at all times; the remaining slots rotate through reels/chat/me.
const ZONES = {
  alix: { key: "alix", label: "ALIX", icon: Sparkles },
  feed: { key: "feed", label: "FEED", icon: Home },
  reels: { key: "reels", label: "REELS", icon: Clapperboard },
  chat: { key: "chat", label: "CHAT", icon: MessageCircle },
  me: { key: "me", label: "ME", icon: User },
};

// Rotating slots. ALIX is pinned; the four content zones (feed/me/reels/chat)
// are always reachable, with the active one highlighted per the current page.
const ROTATOR_ORDER = ["feed", "me", "reels", "chat"];

export function computeRotators(active) {
  // Kept for back-compat: the full content-zone set (active highlighted in-place).
  return ROTATOR_ORDER;
}

function NavButton({ zoneKey, active, onClick, badge }) {
  const z = ZONES[zoneKey];
  if (!z) return <div className="flex-1" />;
  const Icon = z.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`nav-${z.key}`}
      className="relative flex flex-col items-center justify-center gap-1 flex-1 min-w-[48px] min-h-[48px] py-1 group"
    >
      <span className="relative">
        <Icon size={20} strokeWidth={2.2} className={["transition-colors duration-200", active ? "text-gold" : "text-white/55 group-hover:text-white/80"].join(" ")} />
        {badge > 0 && (
          <span data-testid={`nav-${z.key}-badge`} className="absolute -top-2 -right-2.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white border border-charcoal grid place-items-center font-mono text-[8px] font-black">{badge}</span>
        )}
      </span>
      <span className={["font-mono text-[9px] tracking-[0.18em] transition-colors duration-200", active ? "text-gold" : "text-white/45 group-hover:text-white/70"].join(" ")}>{z.label}</span>
    </button>
  );
}

export default function BottomNav({ view, setView, fabOpen, onFabToggle, socialUnread = 0, hidden = false }) {
  // ALIX pinned + four content zones (feed/me/reels/chat) always reachable,
  // split around the center FAB. Active zone is highlighted per the page.
  const left = ["alix", "feed", "me"];
  const right = ["reels", "chat"];

  const renderBtn = (zoneKey) => (
    <NavButton
      key={zoneKey}
      zoneKey={zoneKey}
      active={view === zoneKey}
      onClick={() => setView(zoneKey)}
      badge={zoneKey === "chat" ? socialUnread : 0}
    />
  );

  return (
    <nav
      className={[
        "relative z-40 shrink-0 overflow-hidden px-3 bg-charcoal transition-all duration-300 ease-in-out md:hidden",
        hidden
          ? "max-h-0 pt-0 pb-0 border-t-0 opacity-0 pointer-events-none"
          : "max-h-24 pt-2 pb-2 border-t-4 border-gold/80 opacity-100",
      ].join(" ")}
      data-testid="bottom-nav"
    >
      <div className="flex items-end justify-between max-w-lg mx-auto">
        {left.map(renderBtn)}

        {/* Center spacer — the FAB is rendered by AppShell above the drawer */}
        <div className="flex-1 flex justify-center" aria-hidden="true">
          <span className="w-14 h-14 block" />
        </div>

        {right.map(renderBtn)}
      </div>
      {/* Software home tracking accent indicator */}
      <div className="mx-auto mt-2 w-24 h-1 rounded-full bg-cream/25" data-testid="nav-home-indicator" />
    </nav>
  );
}
